import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import { Suspense, lazy } from 'react'
import { PageSkeleton } from '@/components/tutara-ui/core/TutaraSkeleton'
import { DashboardSkeleton } from '@/pages/learner/DashboardSkeleton'
import { PageLoadingBar } from '@/components/layout/PageLoadingBar'
import ScrollToTop from '@/components/layout/ScrollToTop'

// Auth components
import AuthProvider from '@/components/auth/AuthProvider'
import ProtectedRoute from '@/components/auth/ProtectedRoute'
import TenantStatusSync from '@/components/auth/TenantStatusSync'
import EnterpriseGate from '@/components/auth/EnterpriseGate'

// Context providers
import { SidebarProvider } from '@/contexts/SidebarContext'

// Hooks
import { useOrganizationSync } from '@/hooks/useOrganizationSync'

// Cross-portal host detection (Phase 4 — subdomain split)
import { isAdminHost } from '@/config/portals'

// Layouts
import LandingLayout from '@/components/layouts/LandingLayout'
import AppLayout from '@/components/layouts/AppLayout'
import AdminLayout from '@/components/layouts/AdminLayout'
import AuthLayout from '@/components/layouts/AuthLayout'
import SuperAdminLayout from '@/components/layouts/SuperAdminLayout'

// Super-admin platform console (Phase 5 — mounted on the `admin.` host
// instead of the tenant-admin subdomain flow; see isAdminHost() below).
import SuperAdminGate from '@/pages/superadmin/SuperAdminGate'
const AccountsPage = lazy(() => import('@/pages/superadmin/AccountsPage'))
const AccountDetailPage = lazy(() => import('@/pages/superadmin/AccountDetailPage'))
const TokenUsageDashboardPage = lazy(() => import('@/pages/superadmin/TokenUsageDashboardPage'))

// Pages (lazy loaded for code splitting)
const HomePage = lazy(() => import('@/pages/public/HomePage'))
const AboutPage = lazy(() => import('@/pages/public/AboutPage'))
const ContactPage = lazy(() => import('@/pages/public/ContactPage'))
const PrivacyPolicyPage = lazy(() => import('@/pages/public/PrivacyPolicyPage'))
const TermsOfServicePage = lazy(() => import('@/pages/public/TermsOfServicePage'))
const CookiePolicyPage = lazy(() => import('@/pages/public/CookiePolicyPage'))
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const SignupPage = lazy(() => import('@/pages/auth/SignupPage'))
const OAuthCallbackPage = lazy(() => import('@/pages/auth/OAuthCallbackPage'))
const OrganizationSetupPage = lazy(() => import('@/pages/auth/OrganizationSetupPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const AccountSuspendedPage = lazy(() => import('@/pages/auth/AccountSuspendedPage'))
const JoinRequestStatusPage = lazy(() => import('@/pages/auth/JoinRequestStatusPage'))
const SetPasswordPage = lazy(() => import('@/pages/auth/SetPasswordPage'))
const PendingMembersPage = lazy(() => import('@/pages/admin/PendingMembersPage'))
const DashboardPage = lazy(() => import('@/pages/learner/DashboardPage'))
const LessonViewerPage = lazy(() => import('@/pages/app/LessonViewerPage'))
const MicrolearningPage = lazy(() => import('@/pages/app/MicrolearningPage'))
const PracticeQuizPage = lazy(() => import('@/pages/app/PracticeQuizPage'))
const TopicViewerPage = lazy(() => import('@/pages/app/TopicViewerPage'))
const CourseDetailPage = lazy(() => import('@/pages/app/CourseDetailPage'))

// App pages
const MyLearningPage = lazy(() => import('@/pages/app/MyLearningPage'))
const AchievementsPage = lazy(() => import('@/pages/app/AchievementsPage'))
const ProgressPage = lazy(() => import('@/pages/app/ProgressPage'))
const ProfilePage = lazy(() => import('@/pages/app/ProfilePage'))
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'))

// Alpha: Hidden placeholder pages (uncomment when ready)
// const SchedulePage = lazy(() => import('@/pages/app/SchedulePage'))
// const ResourcesPage = lazy(() => import('@/pages/app/ResourcesPage'))
// const CommunityPage = lazy(() => import('@/pages/app/CommunityPage'))
// const QuickLearnPage = lazy(() => import('@/pages/app/QuickLearnPage'))
// const SettingsPage = lazy(() => import('@/pages/app/SettingsPage'))

// New learner pages
const MyProgramsPage = lazy(() => import('@/pages/learner/MyProgramsPage'))
const ExplorePage = lazy(() => import('@/pages/learner/ExplorePage'))
const DocumentViewerPage = lazy(() => import('@/pages/learner/DocumentViewerPage'))
const SkillsMapPageV2 = lazy(() => import('@/pages/learner/SkillsMapPageV2'))
const SkillsMapPageV3 = lazy(() => import('@/pages/learner/SkillsMapPageV3'))
const FolderViewPage = lazy(() => import('@/pages/learner/FolderViewPage'))
const PersonalFolderViewPage = lazy(() => import('@/pages/learner/PersonalFolderViewPage'))
const MyDocumentsPage = lazy(() => import('@/pages/learner/MyDocumentsPage'))
const StudyPlanViewPage = lazy(() => import('@/pages/learner/StudyPlanViewPage'))
const MyStudyPlansPage = lazy(() => import('@/pages/learner/MyStudyPlansPage'))
const ConceptLabPage = lazy(() => import('@/pages/app/ConceptLabPage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

// Onboarding pages
const LearnerOnboardingPage = lazy(() => import('@/pages/learner/OnboardingPage'))

// Multi-tenant pages (public routes)
const InvitationAcceptPage = lazy(() => import('@/pages/multi-tenant/InvitationAcceptPage'))

// Alpha: Multi-tenant pages (hidden - uncomment when ready)
// const TenantSettingsPage = lazy(() => import('@/pages/multi-tenant/TenantSettingsPage'))
// const TenantDomainsPage = lazy(() => import('@/pages/multi-tenant/TenantDomainsPage'))
// const TenantOrganizationsPage = lazy(() => import('@/pages/multi-tenant/TenantOrganizationsPage'))
// const OrganizationSettingsPage = lazy(() => import('@/pages/multi-tenant/OrganizationSettingsPage'))
const OrganizationMembersPage = lazy(() => import('@/pages/multi-tenant/OrganizationMembersPage'))
const InvitationsManagementPage = lazy(() => import('@/pages/multi-tenant/InvitationsManagementPage'))

// Enterprise admin console pages — ADMIN + ENTERPRISE plan gated, mounted under
// /admin/enterprise/*. Live in src/pages/admin/console/** (Phase 3 split the real
// console away from the /demo mock client demo that used to share this tree).
const OrgDashboard = lazy(() => import('@/pages/admin/console/dashboard/OrgDashboard'))
const Assessments = lazy(() => import('@/pages/admin/console/team/Assessments'))
const AssessmentReview = lazy(() => import('@/pages/admin/console/team/AssessmentReview'))
const EmployeeManagement = lazy(() => import('@/pages/admin/console/EmployeeManagement'))
const PeopleManagement = lazy(() => import('@/pages/admin/console/PeopleManagement'))
const OrganizationSettings = lazy(() => import('@/pages/admin/console/OrganizationSettings'))
const TeamManagement = lazy(() => import('@/pages/admin/console/TeamManagement'))
const ProgramsListPage = lazy(() => import('@/pages/admin/console/programs/ProgramsListPage'))
const ContentLibraryPage = lazy(() => import('@/pages/admin/console/content/ContentLibraryPage'))
const SkillsTaxonomyPage = lazy(() => import('@/pages/admin/console/skills/SkillsTaxonomyPage'))
const ReportsPage = lazy(() => import('@/pages/admin/console/reports/ReportsPage'))

// Tutor agentic-authoring pages (see review/AGENTIC_COURSE_AUTHORING_PLAN.md)
const OutlineEditorPage = lazy(() => import('@/pages/tutor/OutlineEditorPage'))
const OutlineDashboardPage = lazy(() => import('@/pages/tutor/OutlineDashboardPage'))
// Phase 4a — dashboard expansion
const TutorDashboardOverviewPage = lazy(() => import('@/pages/tutor/TutorDashboardOverviewPage'))
const EmployeeDeepDivePage = lazy(() => import('@/pages/tutor/EmployeeDeepDivePage'))

// Course Studio (Document-Sourced Course Generation — Phase 3)
const CourseStudioPage = lazy(() => import('@/pages/admin/CourseStudio/CourseStudioPage'))
const CourseStudioUploadWizard = lazy(() => import('@/pages/admin/CourseStudio/CourseStudioUploadWizard'))
const CourseStudioGenerate = lazy(() => import('@/pages/admin/CourseStudio/CourseStudioGenerate'))
const CourseStudioReview = lazy(() => import('@/pages/admin/CourseStudio/CourseStudioReview'))

// Employee consumption loop (Phase 2b — see review/specs/2026-04-30-phase2b-employee-frontend-design.md)
const StartCoursePage = lazy(() => import('@/pages/employee/StartCoursePage'))
const LearnCoursePage = lazy(() => import('@/pages/employee/LearnCoursePage'))

// Enterprise prototype pages — client demo (see docs/plans/ENTERPRISE_COURSE_PROTOTYPE_PLAN.md).
// Phase 3 relocated these mock/demo pages to src/pages/demo/** to disambiguate them
// from the real admin console (src/pages/admin/console/**).
const HrDashboard = lazy(() => import('@/pages/demo/hr/HrDashboard'))
const CreateCourse = lazy(() => import('@/pages/demo/hr/CreateCourse'))
const CoursePreview = lazy(() => import('@/pages/demo/hr/CoursePreview'))
const CourseAnalytics = lazy(() => import('@/pages/demo/hr/CourseAnalytics'))
const MyCourses = lazy(() => import('@/pages/demo/employee/MyCourses'))
const PersonalizedCourseView = lazy(() => import('@/pages/demo/employee/PersonalizedCourseView'))
const EnterprisePrototypeLayout = lazy(() => import('@/components/layouts/EnterprisePrototypeLayout'))

// Development/Showcase pages
const ComponentShowcase = lazy(() => import('@/pages/ComponentShowcase').then(module => ({ default: module.ComponentShowcase })))
const ColorUsageGuide = lazy(() => import('@/pages/ColorUsageGuide').then(module => ({ default: module.ColorUsageGuide })))
const ColorPaletteShowcase = lazy(() => import('@/pages/ColorPaletteShowcase'))

/**
 * Redirects a legacy top-level admin path to its new home under /admin/*.
 * Phase 3 folded /tutor/* and /organization/:orgId/* under the admin shell;
 * this preserves any bookmarked old URLs by prefixing /admin (keeping the
 * remaining path, search, and hash intact).
 */
function RedirectToAdmin() {
  const location = useLocation()
  return <Navigate to={`/admin${location.pathname}${location.search}${location.hash}`} replace />
}

function App() {
  // Auto-sync organization from authenticated user
  useOrganizationSync();

  // Phase 4: on the `admin.` subdomain the root serves the admin console rather
  // than the public marketing site. Auth routes (/login, /auth/callback) stay
  // available on both hosts so login works from either origin.
  const onAdminHost = isAdminHost();

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <SidebarProvider>
            <PageLoadingBar />
            <ScrollToTop />
            <TenantStatusSync />
            <Suspense fallback={<PageSkeleton />}>
            <Routes>
            {/* Public Routes with Landing Layout. On the admin subdomain the
                root goes straight to the super-admin platform console (Phase
                5) instead of the marketing site — that host no longer runs
                the tenant-admin flow at all. */}
            {onAdminHost ? (
              <>
                <Route path="/" element={<Navigate to="/accounts" replace />} />

                {/* Super-admin shell — SUPER_ADMIN-gated via SuperAdminGate,
                    so children need no per-route guard. */}
                <Route element={<SuperAdminGate><SuperAdminLayout /></SuperAdminGate>}>
                  <Route path="/accounts" element={<AccountsPage />} />
                  <Route path="/accounts/:tenantId" element={<AccountDetailPage />} />
                  <Route path="/token-usage" element={<TokenUsageDashboardPage />} />
                </Route>
              </>
            ) : (
              <Route path="/" element={<LandingLayout />}>
                <Route index element={<HomePage />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="privacy" element={<PrivacyPolicyPage />} />
                <Route path="terms" element={<TermsOfServicePage />} />
                <Route path="cookies" element={<CookiePolicyPage />} />
              </Route>
            )}

            {/* Auth Routes with Auth Layout. Stay mounted on both hosts so
                login works from either origin (the admin host needs it for
                SUPER_ADMIN login; the main host for everyone else). */}
            <Route path="/" element={<AuthLayout />}>
              <Route path="login" element={<LoginPage />} />
              <Route path="signup" element={<SignupPage />} />
              <Route path="auth/callback" element={<OAuthCallbackPage />} />
              <Route path="organization-setup" element={<OrganizationSetupPage />} />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
              {/* Shown when a login response's tenantStatus is suspended/deleted
                  (see LoginPage/OAuthCallbackPage) — reachable regardless of host. */}
              <Route path="account-suspended" element={<AccountSuspendedPage />} />
            </Route>

            {/* Onboarding route (requires authentication) */}
            <Route path="/onboarding" element={<LearnerOnboardingPage />} />

            {/* Multi-tenant invitation route (public) */}
            <Route path="/invitation/:token" element={<InvitationAcceptPage />} />

            {/* Admin-approval join-request routes (public; token in URL is the credential) */}
            <Route path="/join-request/:token" element={<JoinRequestStatusPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />

            {/* App Routes with App Layout - Protected */}
            <Route path="/" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route path="dashboard" element={<Suspense fallback={<DashboardSkeleton />}><DashboardPage /></Suspense>} />
              <Route path="lesson/:id" element={<LessonViewerPage />} />
              <Route path="microlearning" element={<MicrolearningPage />} />
              <Route path="quiz/:id" element={<PracticeQuizPage />} />

              {/* Topic Viewer Route */}
              <Route path="courses/:courseId/modules/:moduleNum/topics/:topicNum" element={<TopicViewerPage />} />

              {/* Course Detail Route */}
              <Route path="courses/:courseId" element={<CourseDetailPage />} />

              {/* Main App Pages */}
              <Route path="my-learning" element={<MyLearningPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="notifications" element={<NotificationsPage />} />

              {/* Progress & Achievements - wired to real backend APIs */}
              <Route path="achievements" element={<AchievementsPage />} />
              <Route path="progress" element={<ProgressPage />} />

              {/* Alpha: Hidden placeholder pages - redirect to dashboard */}
              {/* <Route path="schedule" element={<SchedulePage />} /> */}
              {/* <Route path="resources" element={<ResourcesPage />} /> */}
              {/* <Route path="community" element={<CommunityPage />} /> */}
              {/* <Route path="quick-learn" element={<QuickLearnPage />} /> */}
              {/* <Route path="microlearning" element={<MicrolearningPage />} /> */}
              {/* <Route path="quiz/:id" element={<PracticeQuizPage />} /> */}
              <Route path="schedule" element={<Navigate to="/dashboard" replace />} />
              <Route path="resources" element={<Navigate to="/dashboard" replace />} />
              <Route path="community" element={<Navigate to="/dashboard" replace />} />
              <Route path="quick-learn" element={<Navigate to="/dashboard" replace />} />
              <Route path="settings" element={<Navigate to="/profile" replace />} />

              {/* New Learner Pages */}
              <Route path="my-programs" element={<MyProgramsPage />} />
              <Route path="explore" element={<ExplorePage />} />
              <Route path="explore/my-documents" element={<MyDocumentsPage />} />
              <Route path="explore/folder/:folderId" element={<PersonalFolderViewPage />} />
              <Route path="explore/library" element={<FolderViewPage />} />
              <Route path="explore/library/:folderId" element={<FolderViewPage />} />
              <Route path="explore/study-plan" element={<StudyPlanViewPage />} />
              <Route path="study-plans" element={<MyStudyPlansPage />} />
              <Route path="study-plans/:id" element={<StudyPlanViewPage />} />
              <Route path="explore/:documentId" element={<DocumentViewerPage />} />
              <Route path="skills-map" element={<Navigate to="/skills" replace />} />
              <Route path="skills" element={<SkillsMapPageV2 />} />
              <Route path="career-roadmap" element={<SkillsMapPageV3 />} />
              <Route path="concept-lab" element={<ConceptLabPage />} />

              {/* Admin console routes (/admin/*, /tutor/*) now render under the
                  dedicated AdminLayout shell — see the sibling routes below the
                  AppLayout parent. */}

              {/* Employee consumption loop — every authenticated user; backend dataScope=SELF */}
              <Route path="my-courses" element={<Navigate to="/my-learning?source=assigned-course" replace />} />
              <Route path="my-courses/:assignmentId/start" element={<StartCoursePage />} />
              <Route path="my-courses/:assignmentId/learn" element={<LearnCoursePage />} />

              {/* Enterprise prototype (client demo) — UI-only, mock data */}
              <Route path="enterprise" element={<EnterprisePrototypeLayout />}>
                <Route index element={<Navigate to="hr/dashboard" replace />} />
                <Route path="hr/dashboard" element={<HrDashboard />} />
                <Route path="hr/courses/create" element={<CreateCourse />} />
                <Route path="hr/courses/:id/preview" element={<CoursePreview />} />
                <Route path="hr/courses/:id/analytics" element={<CourseAnalytics />} />
                <Route path="my-courses" element={<MyCourses />} />
                <Route path="my-courses/:id" element={<PersonalizedCourseView />} />
              </Route>
              <Route path="enterprise/*" element={<Navigate to="/enterprise/hr/dashboard" replace />} />

              {/* Multi-tenant management routes - Hidden for alpha */}
              {/* TODO: Re-enable when multi-tenant features are ready */}
              {/* <Route path="tenant">
                <Route path="settings" element={<TenantSettingsPage />} />
                <Route path="domains" element={<TenantDomainsPage />} />
                <Route path="organizations" element={<TenantOrganizationsPage />} />
              </Route> */}
              <Route path="tenant/*" element={<Navigate to="/dashboard" replace />} />

              {/* Org members + invitations management moved under the admin shell
                  (/admin/organization/:orgId/*) in Phase 3. Legacy /organization/*
                  URLs are redirected there via <RedirectToAdmin> below. */}

              {/* Development/Showcase Routes */}
              <Route path="showcase" element={<ComponentShowcase />} />
              <Route path="colors" element={<ColorUsageGuide />} />
              <Route path="palette" element={<ColorPaletteShowcase />} />

              {/* 404 - Page not found (authenticated users) */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Admin console shell (Phase 2) — dedicated AdminLayout, zero learner
                chrome. ADMIN-gated at the parent, so children need no per-route
                guard. URLs are unchanged (/admin/enterprise/*, /admin/course-studio,
                /admin/pending-members); only the surrounding shell changed. */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRoles={['ADMIN']}>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="enterprise/dashboard" replace />} />

              {/* Enterprise console (ADMIN + ENTERPRISE plan). Non-enterprise
                  tenants are bounced to /dashboard by <EnterpriseGate>. */}
              <Route
                path="enterprise"
                element={
                  <EnterpriseGate fallback={<Navigate to="/dashboard" replace />}>
                    <Outlet />
                  </EnterpriseGate>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<OrgDashboard />} />
                <Route path="programs" element={<ProgramsListPage />} />
                <Route path="content" element={<ContentLibraryPage />} />
                <Route path="content/library" element={<Navigate to="/admin/enterprise/content" replace />} />
                <Route path="content/courses" element={<Navigate to="/admin/course-studio" replace />} />
                <Route path="content/upload" element={<Navigate to="/admin/course-studio" replace />} />
                <Route path="content/transform" element={<Navigate to="/admin/course-studio" replace />} />
                <Route path="skills" element={<SkillsTaxonomyPage />} />
                <Route path="people" element={<PeopleManagement />} />
                <Route path="employees" element={<EmployeeManagement />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="organization" element={<OrganizationSettings />} />
                <Route path="organization-settings" element={<OrganizationSettings />} />
                <Route path="team-management" element={<TeamManagement />} />
                <Route path="teams" element={<TeamManagement />} />
                <Route path="assessments" element={<Assessments />} />
                <Route path="assessments/:id/review" element={<AssessmentReview />} />
              </Route>

              {/* Admin join-request approval */}
              <Route path="pending-members" element={<PendingMembersPage />} />

              {/* Course Studio — Document-Sourced Course Generation (Phase 3).
                  Static `new` must precede `:setId` so it is not captured by the param route. */}
              <Route path="course-studio" element={<CourseStudioPage />} />
              <Route path="course-studio/new" element={<CourseStudioUploadWizard />} />
              <Route path="course-studio/:setId" element={<CourseStudioUploadWizard />} />
              <Route path="course-studio/:setId/generate" element={<CourseStudioGenerate />} />
              <Route path="course-studio/courses/:courseId/review" element={<CourseStudioReview />} />

              {/* Tutor agentic-authoring — folded under /admin/tutor/* in Phase 3.
                  Legacy /tutor/* URLs redirect here via <RedirectToAdmin> below. */}
              <Route path="tutor/outlines" element={<Navigate to="/admin/course-studio" replace />} />
              <Route path="tutor/outlines/:id" element={<OutlineEditorPage />} />
              <Route path="tutor/outlines/:outlineId/dashboard" element={<OutlineDashboardPage />} />
              <Route path="tutor/outlines/:outlineId/dashboard/employees/:employeeUserId" element={<EmployeeDeepDivePage />} />
              <Route path="tutor/dashboard" element={<TutorDashboardOverviewPage />} />

              {/* Org members + invitations management — folded under /admin/organization/*
                  in Phase 3 (parent is already ADMIN-gated, so no per-route guard). */}
              <Route path="organization/:orgId/members" element={<OrganizationMembersPage />} />
              <Route path="organization/:orgId/invitations" element={<InvitationsManagementPage />} />

              {/* 404 within the admin shell */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Legacy admin-path redirects → /admin/* (Phase 3 consolidation) */}
            <Route path="/tutor/*" element={<RedirectToAdmin />} />
            <Route path="/organization/*" element={<RedirectToAdmin />} />

            {/* Catch all - redirect to login (unauthenticated users) */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
        </SidebarProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  )
}

export default App
